import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type {
  CertificateRequestFormData,
  CertificateRequestTypeKey,
} from '../types/applicationForm';

export const CERTIFICATE_LIBRARY_BUCKET = 'certificate-library';
const TABLE = 'certificate_files';

export type CertificateLibraryType = Extract<
  CertificateRequestTypeKey,
  'sfa' | 'iso_22000' | 'food_grade_sgs' | 'msds'
>;

export type CertificateFileRow = {
  id: string;
  /** Product: food_grade_sgs | msds. Company: sfa | iso_22000 | custom slug. */
  cert_type: string;
  /** Display name for company certificates (especially custom types). */
  cert_label: string | null;
  item_no: string;
  vendor_no: string;
  /** Canonical match key (e.g. paper, pp, pet lid). */
  material: string;
  valid_from: string | null;
  valid_until: string | null;
  storage_path: string | null;
  file_name: string | null;
  unavailable_reason: string | null;
  active: boolean;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
};

export type CertificateDownloadStatus = 'ready' | 'expired' | 'missing' | 'not_available';

export type CertificateDownloadEntry = {
  key: string;
  cert_type: string;
  label: string;
  item_no: string;
  vendor_no: string;
  material: string;
  file_name: string | null;
  valid_until: string | null;
  unavailable_reason: string | null;
  status: CertificateDownloadStatus;
  storage_path: string | null;
};

export type MaterialKeySlot = {
  key: string;
  label: string;
};

export const COMPANY_TYPES = ['sfa', 'iso_22000'] as const satisfies readonly CertificateLibraryType[];
export const PRODUCT_TYPES = ['food_grade_sgs', 'msds'] as const satisfies readonly CertificateLibraryType[];

export type CompanyCertificateType = (typeof COMPANY_TYPES)[number];
export type ProductCertificateType = (typeof PRODUCT_TYPES)[number];

const TYPE_LABEL: Record<CertificateLibraryType, string> = {
  sfa: 'SFA',
  iso_22000: 'ISO 22000',
  food_grade_sgs: 'Food Grade (SGS)',
  msds: 'MSDS',
};

export function isProductCertificateType(certType: string): certType is ProductCertificateType {
  return (PRODUCT_TYPES as readonly string[]).includes(certType);
}

export function isCompanyCertificateType(certType: string): boolean {
  return !isProductCertificateType(certType);
}

/** True when a free-text company cert name should map to built-in `sfa`. */
export function isSfaCompanyAlias(labelOrSlug: string): boolean {
  const lower = labelOrSlug.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!lower) return false;
  if (lower === 'sfa') return true;
  if (lower.includes('singapore food agency')) return true;
  // e.g. "Singapore Food Agency (SFA)", "company_singapore_food_agency_sfa"
  if (/(^|[^a-z0-9])sfa([^a-z0-9]|$)/.test(lower)) return true;
  return false;
}

/** True when a free-text company cert name should map to built-in `iso_22000`. */
export function isIso22000CompanyAlias(labelOrSlug: string): boolean {
  const lower = labelOrSlug.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!lower) return false;
  return /iso[\s:_-]*22000/.test(lower);
}

/** Map a user-entered company cert name to a stable cert_type slug. */
export function slugifyCompanyCertType(label: string): string {
  const raw = label.trim();
  if (!raw) return '';
  const lower = raw.toLowerCase().replace(/\s+/g, ' ');
  if (isSfaCompanyAlias(lower)) return 'sfa';
  if (isIso22000CompanyAlias(lower)) return 'iso_22000';
  const slug = lower
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 60);
  if (!slug || slug === 'food_grade_sgs' || slug === 'msds') return '';
  if (slug === 'sfa' || slug === 'iso_22000') return slug;
  return `company_${slug}`;
}

/** Find company-wide library row for a built-in CTR type, including mis-slugged aliases. */
export function findCompanyLibraryRow(
  library: CertificateFileRow[],
  certType: CompanyCertificateType,
): CertificateFileRow | undefined {
  const active = library.filter((row) => row.active);
  const exact = active.find((r) => r.cert_type === certType);
  if (exact) return exact;

  return active.find((row) => {
    if (isProductCertificateType(row.cert_type)) return false;
    if (row.cert_type === certType) return true;
    const hay = `${row.cert_type} ${row.cert_label ?? ''}`;
    if (certType === 'sfa') return isSfaCompanyAlias(hay);
    if (certType === 'iso_22000') return isIso22000CompanyAlias(hay);
    return false;
  });
}

export function certificateDisplayLabel(
  certType: string,
  certLabel?: string | null,
): string {
  const custom = certLabel?.trim();
  if (custom) return custom;
  if (certType in TYPE_LABEL) return TYPE_LABEL[certType as CertificateLibraryType];
  if (certType.startsWith('company_')) {
    return certType
      .slice('company_'.length)
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return certType;
}

const PAPER_KEY = 'paper';

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isCertificateExpired(validUntil: string | null | undefined, today = todayIsoDate()): boolean {
  if (!validUntil) return false;
  return validUntil < today;
}

export function isCertificateUnavailable(row: CertificateFileRow | null | undefined): boolean {
  return Boolean(row?.unavailable_reason?.trim());
}

export function sanitizeCertificateFilePart(value: string): string {
  const cleaned = value
    .trim()
    .replace(/[^\w.\-+()]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return cleaned.slice(0, 80) || 'X';
}

export function buildCertificateFileName(input: {
  cert_type: string;
  cert_label?: string | null;
  vendor_no?: string;
  material?: string;
}): string {
  if (input.cert_type === 'sfa') return 'SFA.pdf';
  if (input.cert_type === 'iso_22000') return 'ISO_22000.pdf';
  if (isCompanyCertificateType(input.cert_type)) {
    const label = certificateDisplayLabel(input.cert_type, input.cert_label);
    return `${sanitizeCertificateFilePart(label)}.pdf`;
  }
  const tag = input.cert_type === 'food_grade_sgs' ? 'TEST' : 'MSDS';
  const vendor = sanitizeCertificateFilePart(input.vendor_no ?? '');
  const material = sanitizeCertificateFilePart(input.material ?? '');
  return `${vendor}_${tag}_${material}.pdf`;
}

function normalizeKeyPart(value: string | null | undefined): string {
  return (value ?? '').trim();
}

/** Collapse whitespace / + / , spacing for equality checks. */
export function normalizeCertificateMatchKey(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s*\+\s*/g, '+')
    .replace(/\s*,\s*/g, ',');
}

function matchKey(a: string, b: string): boolean {
  return normalizeCertificateMatchKey(a) === normalizeCertificateMatchKey(b);
}

export function isPaperMaterialPart(part: string): boolean {
  return /\bpaper\b/i.test(part.trim());
}

/** Split master material on + or , into raw parts. */
export function splitRawMaterialParts(material: string | null | undefined): string[] {
  const raw = (material ?? '').trim();
  if (!raw) return [];
  return raw
    .split(/\s*[+,]\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Resolve library material keys from item-master material text.
 * - kraft paper / 280gsm paper → single key `paper` (same vendor shares one cert)
 * - "PP + PE" / "PP cup, PET lid" → one key per part (upload each; sometimes only one exists)
 */
export function resolveMaterialKeys(material: string | null | undefined): MaterialKeySlot[] {
  const parts = splitRawMaterialParts(material);
  if (!parts.length) return [];

  if (parts.length === 1) {
    if (isPaperMaterialPart(parts[0])) {
      return [{ key: PAPER_KEY, label: 'Paper' }];
    }
    return [{ key: normalizeCertificateMatchKey(parts[0]), label: parts[0] }];
  }

  const slots: MaterialKeySlot[] = [];
  let paperAdded = false;
  for (const part of parts) {
    if (isPaperMaterialPart(part)) {
      if (!paperAdded) {
        slots.push({ key: PAPER_KEY, label: 'Paper' });
        paperAdded = true;
      }
      continue;
    }
    const key = normalizeCertificateMatchKey(part);
    if (!slots.some((s) => s.key === key)) {
      slots.push({ key, label: part });
    }
  }
  return slots;
}

export function materialKeyLabel(key: string): string {
  if (key === PAPER_KEY) return 'Paper';
  return key;
}

async function deactivateRivals(
  db: ReturnType<typeof getDbSupabase>,
  certType: string,
  vendorNo: string,
  materialKey: string,
  isCompany: boolean,
): Promise<void> {
  const existing = await listCertificateFiles({ cert_type: certType, activeOnly: true });
  const rivals = existing.filter((row) => {
    if (isCompany) return true; // already scoped to this cert_type
    return matchKey(row.vendor_no, vendorNo) && matchKey(row.material, materialKey);
  });
  for (const rival of rivals) {
    const { error } = await db
      .from(TABLE)
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', rival.id);
    if (error) throw new Error(formatSupabaseError(error));
  }
}

export async function listCertificateFiles(opts?: {
  cert_type?: string;
  activeOnly?: boolean;
}): Promise<CertificateFileRow[]> {
  const db = getDbSupabase('forms');
  let q = db.from(TABLE).select('*').order('updated_at', { ascending: false });
  if (opts?.cert_type) q = q.eq('cert_type', opts.cert_type);
  if (opts?.activeOnly !== false) q = q.eq('active', true);
  const { data, error } = await q;
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as CertificateFileRow[];
}

export async function listCompanyCertificateFiles(): Promise<CertificateFileRow[]> {
  const all = await listCertificateFiles({ activeOnly: true });
  return all.filter((r) => isCompanyCertificateType(r.cert_type));
}

export async function uploadCertificateFile(input: {
  cert_type: string;
  cert_label?: string | null;
  item_no?: string;
  vendor_no?: string;
  /** Canonical material key (use resolveMaterialKeys). */
  material?: string;
  valid_from?: string | null;
  valid_until: string;
  file: File;
  uploaded_by?: string | null;
}): Promise<CertificateFileRow> {
  const certType = normalizeKeyPart(input.cert_type);
  if (!certType) throw new Error('Certificate type is required.');
  const isCompany = isCompanyCertificateType(certType);
  const certLabel = isCompany
    ? normalizeKeyPart(input.cert_label) || certificateDisplayLabel(certType, input.cert_label)
    : null;
  const itemNo = isCompany ? '' : normalizeKeyPart(input.item_no);
  const vendorNo = isCompany ? '' : normalizeKeyPart(input.vendor_no);
  const material = isCompany ? '' : normalizeCertificateMatchKey(input.material);
  const validUntil = normalizeKeyPart(input.valid_until);
  const validFrom = normalizeKeyPart(input.valid_from ?? '') || todayIsoDate();

  if (!validUntil) throw new Error('Validity end date is required.');
  if (!input.file || input.file.size <= 0) throw new Error('PDF file is required.');
  if (input.file.type && input.file.type !== 'application/pdf') {
    throw new Error('Only PDF files are allowed.');
  }
  if (!isCompany && (!vendorNo || !material)) {
    throw new Error('Vendor no. and material are required.');
  }
  if (isCompany && !certLabel) {
    throw new Error('Certificate name is required.');
  }

  const fileName = buildCertificateFileName({
    cert_type: certType,
    cert_label: certLabel,
    vendor_no: vendorNo,
    material,
  });
  const storagePath = `${certType}/${crypto.randomUUID()}.pdf`;
  const db = getDbSupabase('forms');

  const { error: uploadError } = await db.storage.from(CERTIFICATE_LIBRARY_BUCKET).upload(storagePath, input.file, {
    upsert: false,
    contentType: 'application/pdf',
  });
  if (uploadError) throw new Error(uploadError.message || 'Failed to upload PDF.');

  try {
    await deactivateRivals(db, certType, vendorNo, material, isCompany);
  } catch (e) {
    await db.storage.from(CERTIFICATE_LIBRARY_BUCKET).remove([storagePath]);
    throw e;
  }

  const { data, error } = await db
    .from(TABLE)
    .insert({
      cert_type: certType,
      cert_label: certLabel,
      item_no: itemNo,
      vendor_no: vendorNo,
      material,
      valid_from: validFrom,
      valid_until: validUntil,
      storage_path: storagePath,
      file_name: fileName,
      unavailable_reason: null,
      active: true,
      uploaded_by: input.uploaded_by?.trim() || null,
    })
    .select('*')
    .single();

  if (error) {
    await db.storage.from(CERTIFICATE_LIBRARY_BUCKET).remove([storagePath]);
    throw new Error(formatSupabaseError(error));
  }

  return data as CertificateFileRow;
}

/** Mark vendor+material+type as not available (no PDF); reason shown on posted CTR. */
export async function markCertificateUnavailable(input: {
  cert_type: CertificateLibraryType;
  item_no?: string;
  vendor_no: string;
  material: string;
  reason: string;
  uploaded_by?: string | null;
}): Promise<CertificateFileRow> {
  const certType = input.cert_type;
  const vendorNo = normalizeKeyPart(input.vendor_no);
  const material = normalizeCertificateMatchKey(input.material);
  const reason = normalizeKeyPart(input.reason);
  if (!vendorNo || !material) throw new Error('Vendor no. and material are required.');
  if (!reason) throw new Error('Please enter a reason for Not available.');

  const db = getDbSupabase('forms');
  await deactivateRivals(db, certType, vendorNo, material, false);

  const { data, error } = await db
    .from(TABLE)
    .insert({
      cert_type: certType,
      item_no: normalizeKeyPart(input.item_no),
      vendor_no: vendorNo,
      material,
      valid_from: null,
      valid_until: null,
      storage_path: null,
      file_name: null,
      unavailable_reason: reason,
      active: true,
      uploaded_by: input.uploaded_by?.trim() || null,
    })
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return data as CertificateFileRow;
}

export async function deactivateCertificateFile(id: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db
    .from(TABLE)
    .update({ active: false, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function getCertificateSignedUrl(storagePath: string): Promise<string> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage
    .from(CERTIFICATE_LIBRARY_BUCKET)
    .createSignedUrl(storagePath, 60 * 30);
  if (error) throw new Error(error.message || 'Failed to open certificate.');
  if (!data?.signedUrl) throw new Error('Failed to open certificate.');
  return data.signedUrl;
}

export function findLibraryRow(
  library: CertificateFileRow[],
  certType: string,
  vendorNo: string,
  materialKey: string,
): CertificateFileRow | undefined {
  return library.find(
    (r) =>
      r.active &&
      r.cert_type === certType &&
      matchKey(r.vendor_no, vendorNo) &&
      matchKey(r.material, materialKey),
  );
}

export type ProductCertBadgeStatus = 'ready' | 'expired' | 'missing';

/**
 * Card badge for one product cert type (TEST / MSDS) across all material slots:
 * - expired: any expired PDF (takes priority even if other slots are ready)
 * - ready: at least one valid PDF, and none expired
 * - missing: otherwise (all missing, all N/A, or N/A + missing)
 */
export function itemProductCertBadgeStatus(
  library: CertificateFileRow[],
  certType: ProductCertificateType,
  vendorNo: string | null | undefined,
  materialRaw: string | null | undefined,
): ProductCertBadgeStatus {
  const vendor = (vendorNo ?? '').trim();
  const keys = resolveMaterialKeys(materialRaw);
  if (!vendor || !keys.length) return 'missing';

  let hasReady = false;
  let hasExpired = false;
  for (const slot of keys) {
    const row = findLibraryRow(library, certType, vendor, slot.key);
    if (!row || isCertificateUnavailable(row)) continue;
    if (!row.storage_path?.trim()) continue;
    if (isCertificateExpired(row.valid_until)) {
      hasExpired = true;
      continue;
    }
    hasReady = true;
  }

  if (hasExpired) return 'expired';
  if (hasReady) return 'ready';
  return 'missing';
}

export function itemProductCertReports(
  library: CertificateFileRow[],
  vendorNo: string | null | undefined,
  materialRaw: string | null | undefined,
): { test: ProductCertBadgeStatus; msds: ProductCertBadgeStatus } {
  return {
    test: itemProductCertBadgeStatus(library, 'food_grade_sgs', vendorNo, materialRaw),
    msds: itemProductCertBadgeStatus(library, 'msds', vendorNo, materialRaw),
  };
}

export function resolveCertificateDownloads(
  form: CertificateRequestFormData,
  library: CertificateFileRow[],
): CertificateDownloadEntry[] {
  const today = todayIsoDate();
  const active = library.filter((row) => row.active);
  const entries: CertificateDownloadEntry[] = [];

  for (const certType of COMPANY_TYPES) {
    if (!form.types[certType]) continue;
    const row = findCompanyLibraryRow(active, certType);
    entries.push(toEntry(certType, '', '', '', row, today));
  }

  for (const certType of PRODUCT_TYPES) {
    if (!form.types[certType]) continue;
    const lines = form.items.filter(
      (item) => item.item_no.trim() || item.vendor_no.trim() || item.material.trim(),
    );
    if (!lines.length) {
      entries.push(toEntry(certType, '', '', '', null, today));
      continue;
    }
    for (const item of lines) {
      const itemNo = item.item_no.trim();
      const vendorNo = item.vendor_no.trim();
      const materialRaw = item.material.trim();
      if (!vendorNo || !materialRaw) {
        entries.push(toEntry(certType, itemNo, vendorNo, materialRaw, null, today));
        continue;
      }
      const keys = resolveMaterialKeys(materialRaw);
      if (!keys.length) {
        entries.push(toEntry(certType, itemNo, vendorNo, materialRaw, null, today));
        continue;
      }
      for (const slot of keys) {
        const row = findLibraryRow(active, certType, vendorNo, slot.key);
        entries.push(toEntry(certType, itemNo, vendorNo, slot.label, row, today, slot.key));
      }
    }
  }

  return entries;
}

function toEntry(
  certType: string,
  itemNo: string,
  vendorNo: string,
  materialLabel: string,
  row: CertificateFileRow | null | undefined,
  today: string,
  materialKey?: string,
): CertificateDownloadEntry {
  const key = [certType, itemNo, vendorNo, materialKey || materialLabel].join('|');
  const labelParts = [certificateDisplayLabel(certType, row?.cert_label)];
  if (itemNo || vendorNo || materialLabel) {
    labelParts.push([itemNo, vendorNo, materialLabel].filter(Boolean).join(' · '));
  }

  if (!row) {
    return {
      key,
      cert_type: certType,
      label: labelParts.join(' — '),
      item_no: itemNo,
      vendor_no: vendorNo,
      material: materialLabel,
      file_name: null,
      valid_until: null,
      unavailable_reason: null,
      status: 'missing',
      storage_path: null,
    };
  }

  if (isCertificateUnavailable(row)) {
    return {
      key,
      cert_type: certType,
      label: labelParts.join(' — '),
      item_no: itemNo,
      vendor_no: vendorNo,
      material: materialLabel,
      file_name: null,
      valid_until: null,
      unavailable_reason: row.unavailable_reason,
      status: 'not_available',
      storage_path: null,
    };
  }

  const expired = isCertificateExpired(row.valid_until, today);
  return {
    key,
    cert_type: certType,
    label: labelParts.join(' — '),
    item_no: itemNo,
    vendor_no: vendorNo,
    material: materialLabel,
    file_name: row.file_name,
    valid_until: row.valid_until,
    unavailable_reason: null,
    status: expired ? 'expired' : 'ready',
    storage_path: expired || !row.storage_path ? null : row.storage_path,
  };
}

export function certificateTypeLabel(
  certType: string,
  certLabel?: string | null,
): string {
  return certificateDisplayLabel(certType, certLabel);
}

export { TYPE_LABEL as CERTIFICATE_LIBRARY_TYPE_LABEL };
